import { CircularProgress } from '@mui/material';
import { ActionButton } from '@pages/ActionButton';
import { useEffect, useState } from 'react';
import css from './AppBootstrapFallback.module.scss';

// Most cold loads resolve in well under this, so the spinner never appears —
// avoids a flash-of-spinner flicker on a normal fast bootstrap. It only shows
// when the session genuinely takes a while (the slow-network case).
const SPINNER_DELAY_MS = 250;

/**
 * Full-page fallback shown while the app boots the user session (auth check /
 * billing prefetch), and when that bootstrap fails on a connectivity error.
 *
 * Replaces the previous `return null` in WithUser, which rendered a bare black
 * screen for the whole (up to ~30s) duration of a hanging bootstrap request —
 * e.g. a transient DNS failure right after wake-from-sleep. A branded loader (or
 * a retry on failure) is far less alarming than a black page.
 */
export const AppBootstrapLoading = () => {
  // Render the dark ground immediately (so there's never a black flash), but
  // hold the spinner back briefly so fast loads show nothing at all.
  const [showSpinner, setShowSpinner] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setShowSpinner(true), SPINNER_DELAY_MS);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className={css.screen}>
      {showSpinner && <CircularProgress size={36} sx={{ color: '#9d4edd' }} />}
    </div>
  );
};

export const AppBootstrapError = ({ onRetry }: { onRetry: () => void }) => (
  <div className={css.screen}>
    <div className={css.message}>
      <h1 className={css.title}>Couldn&apos;t connect</h1>
      <p className={css.body}>
        We couldn&apos;t reach StarZero. Check your internet connection and try again.
      </p>
      <ActionButton variant="primary" onClick={onRetry}>
        Try again
      </ActionButton>
    </div>
  </div>
);
